import { Box, Typography, CircularProgress, IconButton } from "@mui/material";
import { colors } from "../../../../../theme/muiColors";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { SmsMessage } from "../types/SmsMessage";
import { highlightText } from "@components/shared/utils/highlightText";

interface SmsSearchResultsListProps {
    messages: SmsMessage[];
    searchQuery: string;
    sessionNumber: string;
    isLoading: boolean;
    error: unknown;
    isFetchingNextPage: boolean;
    hasNextPage?: boolean;
    onFetchNextPage: () => void;
    onSelectMessage: (message: SmsMessage) => void;
    onOpenActions: (params: {
        anchorEl: HTMLElement;
        message: SmsMessage;
        isOutgoing: boolean;
    }) => void;
}

function getMessagePreview(message: SmsMessage): string {
    const body = message.content?.Body?.trim();

    if (body) return body;

    const mediaUrls: string[] = message.content?.MediaUrl ?? [];

    if (mediaUrls.length === 1) return "Media message";
    if (mediaUrls.length > 1) return `${mediaUrls.length} media attachments`;

    return "Empty message";
}

function getMessageDateLabel(date: Date): string {
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year:
            date.getFullYear() !== new Date().getFullYear()
                ? "numeric"
                : undefined,
    });
}

export function SmsSearchResultsList({
    messages,
    searchQuery,
    sessionNumber,
    isLoading,
    error,
    isFetchingNextPage,
    hasNextPage,
    onFetchNextPage,
    onSelectMessage,
    onOpenActions,
}: SmsSearchResultsListProps) {
    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        if (!hasNextPage || isFetchingNextPage) return;

        const container = event.currentTarget;
        const bottomThreshold = 120;

        const reachedBottom =
            container.scrollHeight - container.scrollTop - container.clientHeight <=
            bottomThreshold;

        if (reachedBottom) {
            onFetchNextPage();
        }
    };

    return (
        <Box
            flex={1}
            overflow="auto"
            onScroll={handleScroll}
            sx={{
                backgroundColor: "background.default",
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                    bgcolor: "divider",
                    borderRadius: "3px",
                    "&:hover": { bgcolor: "action.disabled" },
                },
            }}
        >
            <Box
                px={2}
                py={1.25}
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    bgcolor: "background.paper",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    Search results for
                </Typography>

                <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    “{searchQuery}”
                </Typography>
            </Box>

            {isLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                    <CircularProgress size={26} />
                </Box>
            )}

            {error && (
                <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                    <Box textAlign="center">
                        <ErrorOutlineIcon
                            sx={{ fontSize: 36, color: "error.main", mb: 1 }}
                        />
                        <Typography color="error" variant="body2">
                            Failed to search messages
                        </Typography>
                        <Typography color="text.secondary" variant="caption">
                            Please try again later
                        </Typography>
                    </Box>
                </Box>
            )}

            {!isLoading && !error && messages.length === 0 && (
                <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                    <Typography color={colors.orange[300]} variant="body2">
                        No messages found
                    </Typography>
                </Box>
            )}

            {!isLoading &&
                !error &&
                messages.map((message) => {
                    const isOutgoing = message.from === sessionNumber;
                    const date = new Date(message.createdAt);
                    const preview = getMessagePreview(message);

                    return (
                        <Box
                            key={message.uid}
                            px={2}
                            py={1.25}
                            onClick={() => onSelectMessage(message)}
                            sx={{
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                bgcolor: "background.paper",
                                cursor: "pointer",
                                "&:hover": {
                                    bgcolor: "grey.50",
                                },
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1}>
                                <Box flex={1} minWidth={0}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography
                                            variant="caption"
                                            fontWeight={700}
                                            sx={{
                                                color: isOutgoing
                                                    ? colors.orange[700]
                                                    : "text.secondary",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {isOutgoing ? "You" : "Contact"}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.disabled"
                                            sx={{ flexShrink: 0 }}
                                        >
                                            {getMessageDateLabel(date)}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.disabled"
                                            sx={{ flexShrink: 0 }}
                                        >
                                            {date.toLocaleTimeString("en-US", {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </Typography>
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        mt={0.5}
                                        sx={{
                                            color: "text.primary",
                                            overflow: "hidden",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {highlightText(preview, searchQuery)}
                                    </Typography>
                                </Box>

                                <IconButton
                                    size="small"
                                    onClick={(event) => {
                                        event.stopPropagation();

                                        onOpenActions({
                                            anchorEl: event.currentTarget,
                                            message,
                                            isOutgoing,
                                        });
                                    }}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        color: "text.secondary",
                                        flexShrink: 0,
                                    }}
                                >
                                    <MoreHorizIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                            </Box>
                        </Box>
                    );
                })}

            {isFetchingNextPage && (
                <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={20} />
                </Box>
            )}
        </Box>
    );
}